import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { isPlatform } from "@shared/utils/get-platform";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import axios from "axios";
import { useCallback, useState } from "react";
import LoginForm from "../LoginForm/LoginForm.web";
import OtpForm from "../OtpForm/OtpForm.web";
import SsoLoginForm from "../SsoLoginForm/SsoLoginForm.web";
import type { LoginSharedProps } from "./Login.props";

const notifier = NotifierFactory.createNotifier();

type LoginView = "login" | "otp" | "sso";

export default function Login(props: LoginProps): JSX.Element {
  const { onSignupClick, onForgotPasswordClick, onSuccess } = props;
  const { t } = useTranslation("auth");

  const [view, setView] = useState<LoginView>("login");
  const { login } = useUserSessionAuthState();
  const [credentials, setCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const handleOtpRequired = useCallback(
    async (credentials: { email: string; password: string }) => {
      setCredentials(credentials);
      setView("otp");
    },
    []
  );

  const handleVerifyOtp = useCallback(
    async (otp: string) => {
      if (credentials) {
        const { email, password } = credentials;
        const response = await login(email, password, otp);

        if (response && "requires_otp" in response && response.requires_otp) {
          throw new Error("Verification code is invalid");
        }
        onSuccess();
      }
    },
    [credentials, login, onSuccess]
  );

  const handleResendOtp = useCallback(async () => {
    if (credentials) {
      const { email, password } = credentials;
      try {
        const response = await login(email, password);
        if (
          response &&
          "requires_otp" in response &&
          "otp_expiry_step" in response &&
          response.requires_otp &&
          response.otp_expiry_step
        ) {
          notifier.success(t("Verification code resent successfully"));
          return Number(response.otp_expiry_step);
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          // logAppInfo(error.response?.data.detail);
        }
      }
    }
    return undefined;
  }, [credentials, login, t]);

  if (view === "otp") {
    return (
      <Box w="100%">
        <OtpForm
          onVerifyOtp={handleVerifyOtp}
          onOtpResend={handleResendOtp}
          email={credentials?.email ?? ""}
        />
      </Box>
    );
  }

  if (view === "sso") {
    return (
      <Box w="100%">
        <Text textStyle="heading.5xl" fontWeight="bold">
          {t("SSO Login")}
        </Text>
        <Box h="element.2xl" />
        <SsoLoginForm onSuccess={onSuccess} onBack={() => setView("login")} />
      </Box>
    );
  }

  return (
    <Box w="100%">
      <Text textStyle="heading.5xl" fontWeight="bold">
        {t("Log in")}
      </Text>
      <Box h="element.2xl" />
      {!isPlatform("desktop") && (
        <Flex alignItems="center">
          <Text as="span">{t("Don't have an account?")}</Text>
          <Button
            colorPalette="secondary"
            variant="link"
            px="2xs"
            onClick={onSignupClick}
            data-testid="sign-up-form-link"
          >
            {t("Sign up")}
          </Button>
        </Flex>
      )}
      <Box h="element.2xl" />
      <LoginForm
        onSuccess={onSuccess}
        onOtpRequired={handleOtpRequired}
        onForgotPasswordClick={onForgotPasswordClick}
        onSsoClick={() => setView("sso")}
      />
    </Box>
  );
}

export type LoginProps = LoginSharedProps & {
  // Add any web specific props here.
};
